/**
 * Modal réutilisable — Phase 1, incrément 7.
 *
 * Wrapper lit-html minimal pour les boîtes de dialogue (ajout
 * événement, ajout tâche, etc.). Fermeture sur Escape, click outside,
 * ou bouton « ✕ ».
 *
 * Le caller fournit le titre et le contenu. Le modal n'embarque pas
 * d'état métier — c'est un pur wrapper visuel + accessibilité.
 */
import { html, type TemplateResult } from 'lit-html';
import { ref } from 'lit-html/directives/ref.js';
import styles from './Modal.module.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Élément focusé avant l'ouverture — restauré à la fermeture (T2,
// accessibilité : l'utilisateur clavier revient d'où il vient).
let lastFocused: HTMLElement | null = null;

/** Monte/démonte : focus initial + restauration. */
function dialogRef(el: Element | undefined): void {
  if (el instanceof HTMLElement) {
    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Focus le premier élément interactif (souvent le champ du form),
    // sinon le dialog lui-même.
    const first = el.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el).focus();
  } else if (lastFocused) {
    lastFocused.focus();
    lastFocused = null;
  }
}

/** Piège le Tab dans la modale (cycle avant/arrière). */
function trapTab(e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const dialog = e.currentTarget as HTMLElement;
  const focusables = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === dialog)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

export interface ModalOptions {
  title: string;
  content: TemplateResult;
  onClose: () => void;
  /** Libellé accessible du bouton de fermeture. */
  closeLabel?: string;
  /** data-testid du conteneur racine (utile aux tests E2E). */
  testid?: string;
}

export function modalTemplate(opts: ModalOptions): TemplateResult {
  const onBackdrop = (e: Event): void => {
    if (e.target === e.currentTarget) opts.onClose();
  };
  // L'écoute Escape est centralisée dans `stores/ui.ts` (handler global
  // window) — elle fonctionne même si le focus n'est pas dans la modale.
  return html`
    <div
      class=${styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label=${opts.title}
      data-testid=${opts.testid ?? 'modal'}
      @click=${onBackdrop}
    >
      <div class=${styles.dialog} tabindex="-1" ${ref(dialogRef)} @keydown=${trapTab}>
        <header class=${styles.header}>
          <h2 class=${styles.title}>${opts.title}</h2>
          <button
            type="button"
            class=${styles.close}
            aria-label=${opts.closeLabel ?? 'Close'}
            data-testid="modal-close"
            @click=${(): void => opts.onClose()}
          >
            ✕
          </button>
        </header>
        <div class=${styles.body}>${opts.content}</div>
      </div>
    </div>
  `;
}
